import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import RunIcon from "@mui/icons-material/PlayArrow";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { ItemHrchyTargetComponent } from "@plannel/pages/inventory-plan/ip-config/IpSettings";
import { withTranslation } from "react-i18next";
import { isEqual } from "lodash";
import { Checkbox, FormControlLabel } from "@mui/material";
import {useState} from "react";
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";

const { versionStatusCode } = CodeUtil;

const PlanOptionCheckbox = ({ name, label, checked, disabled, onChange }) => (
  <Box sx={{ flex: 5.5, display: "flex", justifyContent: "flex-start" }}>
    <FormControlLabel
      control={
        <Checkbox
          color="primary"
          name={name}
          checked={!!checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
        />
      }
      label={label}
    />
  </Box>
);

const RunModal = ({ t, title, viewName, modalOpen, setModalOpen, setSelectedItemHrchy, selectedPlanVersion, setIsRun, message, useShipmentTxn, onUseShipmentTxnChange, useMpProductionPlan, onUseMpProductionPlan }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const [isCustom, setIsCustom] = useState(false);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { category: null });
  const [snackInfo, setSnackInfo] = useState({open: false, severity: "", content: ""});
  const {snackBarMessageColor} = ColorUtil;

  const isPsiSimulation = title === "confirm"
  const handleClose = (action) => {
    if (isEqual(action, "cancel")) {
      setModalOpen(false);
      const initialValue = selectedPlanVersion?.statusCd === versionStatusCode.PLANNED ? (selectedPlanVersion?.planConfigObj?.USE_SHIPMENT_TXN ?? false) : false;
      onUseShipmentTxnChange?.(initialValue);
    }
  };

  const runAndClose = () => {
    if (isCustom) {
      if (filterValue?.category === null || filterValue?.category.length === 0) {
        setSnackInfo({
          open: true,
          severity: snackBarMessageColor.noMatchCondition,
          content: t("MSG_PleaseEnterValues", {values: t("itemHrchy")})
        });
        return;
      } else {
        const filter = []
        for (const key of filterValue?.category) {
          if (key?.leafFlg) {
            filter.push(key);
          }
        }
        setSelectedItemHrchy(filter);
      }
    } else {
      setSelectedItemHrchy(null);
    }
    setModalOpen(false);
    setIsRun(true);
  }

  return (
    <>
      {/* 고급설정을 켜면 계층 선택 패널이 오른쪽에 붙어 폭이 늘어난다 — 프리셋으로는 표현이 안 돼 width 를 직접 준다 */}
      <BaseConfigModal
        open={modalOpen}
        onClose={() => handleClose("cancel")}
        title={title ? t(title) : t("run")}
        onSave={runAndClose}
        saveLabel={isPsiSimulation ? t("confirm") : t("run")}
        saveIcon={<RunIcon />}
        width={isCustom ? 767 : 452}
      >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "row" }}>
              {/*
                간격은 항목마다 `mt` 를 붙이지 않고 컬럼의 `gap` 으로 준다.
                예전엔 첫 항목에도 `mt: 2` 가 붙어 셸의 DialogContent pt(16) 와 합쳐 **32px** 이 됐다
                — 구분선과 첫 입력칸 사이만 유독 벌어져 보여 지적받았다.
                본문 첫 요소에는 여백을 주지 말 것: 상하좌우 16px 은 셸이 이미 준다.
              */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                {!isPsiSimulation ? (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                      name="version"
                      label={t("version")}
                      fullWidth
                      value={selectedPlanVersion?.name ?? ""}
                      variant="outlined"
                      InputProps={{readOnly: true}}
                    />
                    <TextField
                      name="status"
                      label={t("status")}
                      fullWidth
                      value={selectedPlanVersion?.statusCd ?? ""}
                      variant="outlined"
                      InputProps={{readOnly: true}}
                    />
                    <TextField
                      name="description"
                      label={t("descTxt")}
                      fullWidth
                      value={selectedPlanVersion?.descTxt ?? ""}
                      variant="outlined"
                      multiline
                      rows={2}
                      InputProps={{readOnly: true}}
                    />
                  </Box>
                ) : (
                  <Typography id="modal-modal-title" variant="subtitle1" component="h6">
                    {message}
                  </Typography>
                )}
                {/* 체크박스 묶음 — Checkbox 자체 패딩(9px)이 위아래로 튀어나와 셸 여백과 합쳐지면
                    위는 25px, 아래는 28px 로 보인다. 그만큼 당겨서 16px 로 맞춘다 */}
                <Box sx={{ display: "flex", flexDirection: "column", mt: -1, mb: -1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="advancedSettings"
                        checked={isCustom}
                        onChange={() => setIsCustom(!isCustom)}
                      />
                    }
                    label={t("advancedSettings")}
                  />
                  {!isPsiSimulation ? (
                    <>
                      <PlanOptionCheckbox
                        name="useShipmentTxn"
                        label={t("useShipmentTxn")}
                        checked={useShipmentTxn}
                        disabled={selectedPlanVersion?.statusCd !== versionStatusCode.PLANNED || selectedPlanVersion?.execTs === null}
                        onChange={onUseShipmentTxnChange}
                      />
                      <PlanOptionCheckbox
                        name="useMpProductionPlan"
                        label={t("useMpProductionPlan")}
                        checked={useMpProductionPlan}
                        disabled={selectedPlanVersion?.statusCd !== versionStatusCode.PLANNED || selectedPlanVersion?.execTs === null}
                        onChange={onUseMpProductionPlan}
                      />
                    </>
                  ) : null}
                </Box>
              </Box>
              {isCustom && (
                <Box sx={{ flex: 1, minWidth: 340, pl: 1, pt: 2 }}>
                  <ItemHrchyTargetComponent
                    viewName={viewName}
                    setSimTargetFilterPolicy={() => {
                      // empty because not using default setting function
                    }}
                    setFilterValue={setFilterValue}
                    filterValue={filterValue}
                    filterStyles={{ minHeight: 235, maxHeight: 235 }}
                    disabled={false}
                    label={t("MSG_SelectItemHrchy")}
                  />
                </Box>
              )}
            </Box>
          </Box>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({...snackInfo, open: false})}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(RunModal);
